import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Plus, Webhook, Copy, RotateCcw, AlertTriangle, ExternalLink,
  Play, RefreshCw, Pause, Zap, CheckCircle2, XCircle, Clock, FileJson2, ShieldOff,
} from "lucide-react";

const ALL_EVENTS = [
  { id: "timesheet.submitted", label: "Timesheet submitted" },
  { id: "timesheet.approved",  label: "Timesheet approved" },
  { id: "timesheet.rejected",  label: "Timesheet rejected" },
  { id: "timesheet.revised",   label: "Timesheet revised (resubmitted)" },
  { id: "timesheet.revoked",   label: "Timesheet approval revoked" },
  { id: "period.auto_closed",  label: "Pay period auto-closed" },
  { id: "pto.approved",        label: "PTO approved" },
  { id: "pto.rejected",        label: "PTO rejected" },
];

const STATUS_TONE = {
  active: "bg-success/15 text-success",
  paused: "bg-amber-500/15 text-amber-700",
  failed: "bg-red-500/15 text-red-700",
};

const DELIVERY_STATUS_TONE = {
  succeeded: "bg-success/15 text-success",
  pending: "bg-amber-500/15 text-amber-700",
  failed: "bg-red-500/15 text-red-700",
};

export default function WebhooksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(null); // {id, url, events, secret}
  const [openSub, setOpenSub] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/webhooks");
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pauseOrResume = async (s) => {
    const nextStatus = s.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/tenant/webhooks/${s.id}`, { status: nextStatus });
      toast.success(nextStatus === "paused" ? "Paused" : "Resumed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rotateSecret = async (s) => {
    try {
      const { data } = await api.post(`/tenant/webhooks/${s.id}/rotate-secret`);
      setReveal({ ...data, url: s.url, events: s.events, isRotation: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const testFire = async (s) => {
    try {
      const { data } = await api.post(`/tenant/webhooks/${s.id}/test`, { message: "Test from admin console" });
      const status = data.delivery?.last_response_status;
      const label = data.delivery?.status;
      toast.success(`Test fired · ${label}${status ? ` (HTTP ${status})` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSub = async (s) => {
    try {
      await api.delete(`/tenant/webhooks/${s.id}`);
      toast.success("Subscription deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Integrations</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Webhooks</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Receive real-time HMAC-signed events for timesheet approvals, PTO decisions, and period auto-close.
            Every request retries up to 6 times with exponential backoff.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="webhooks-open-docs">
            <a href="/docs/webhooks" target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-4 w-4" /> Signature docs
            </a>
          </Button>
          <NewWebhook onCreated={(k) => { setReveal(k); load(); }} />
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Subscriptions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last success</TableHead>
                <TableHead>Failures</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No webhook subscriptions yet.</TableCell></TableRow>
              )}
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`webhook-row-${s.id}`}>
                  <TableCell className="max-w-[280px] truncate">
                    <button onClick={() => setOpenSub(s)} className="text-left font-medium text-primary hover:underline" data-testid={`webhook-open-${s.id}`}>
                      {s.url}
                    </button>
                    {s.description && <div className="truncate text-xs text-muted-foreground">{s.description}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {s.events.slice(0, 3).map((e) => (
                        <Badge key={e} variant="outline" className="rounded-full text-[10px]">{e}</Badge>
                      ))}
                      {s.events.length > 3 && <Badge variant="outline" className="rounded-full text-[10px]">+{s.events.length - 3}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-full capitalize ${STATUS_TONE[s.status]}`}>{s.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {s.last_success_at ? new Date(s.last_success_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </TableCell>
                  <TableCell className="text-xs tabular-nums">
                    <span className={s.failure_count > 0 ? "text-red-600" : "text-muted-foreground"}>
                      {s.failure_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => testFire(s)} data-testid={`webhook-test-${s.id}`}>
                        <Zap className="mr-1 h-3.5 w-3.5" /> Test
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => pauseOrResume(s)} data-testid={`webhook-toggle-${s.id}`}>
                        {s.status === "active" ? <><Pause className="mr-1 h-3.5 w-3.5" /> Pause</> : <><Play className="mr-1 h-3.5 w-3.5" /> Resume</>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(s)} data-testid={`webhook-edit-${s.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => rotateSecret(s)} data-testid={`webhook-rotate-${s.id}`}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Rotate
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmDelete(s)} data-testid={`webhook-delete-${s.id}`}>
                        <ShieldOff className="mr-1 h-3.5 w-3.5" /> Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {items.some((s) => s.status === "failed") && (
        <Alert variant="destructive" data-testid="webhooks-failed-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            One or more subscriptions have been auto-paused after 10 consecutive delivery failures. Fix the endpoint and click Resume.
          </AlertDescription>
        </Alert>
      )}

      <RevealSecretDialog data={reveal} onClose={() => setReveal(null)} />
      <DeliveriesDrawer sub={openSub} onClose={() => setOpenSub(null)} />
      <EditWebhook sub={editing} onClose={() => setEditing(null)} onSaved={load} />
      <DeleteDialog sub={confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={deleteSub} />
    </div>
  );
}

function NewWebhook({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState(ALL_EVENTS.slice(0, 3).map((e) => e.id));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setUrl(""); setDescription(""); setEvents(ALL_EVENTS.slice(0, 3).map((e) => e.id)); setErr("");
    }
  }, [open]);

  const toggle = (id) => setEvents((es) => (es.includes(id) ? es.filter((x) => x !== id) : [...es, id]));

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/tenant/webhooks", { url, description, events });
      toast.success("Subscription created");
      setOpen(false);
      onCreated({ ...data, isRotation: false });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-webhook-btn"><Plus className="h-4 w-4" /> New subscription</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="new-webhook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Webhook className="h-5 w-5" /> New webhook subscription</DialogTitle>
          <DialogDescription>We'll POST HMAC-signed JSON to this URL every time a subscribed event fires.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Target URL</Label>
            <Input placeholder="https://api.mycompany.com/hooks/clockin" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="new-webhook-url" />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input placeholder="Payroll integration prod" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-webhook-desc" />
          </div>
          <div className="space-y-2">
            <Label>Events</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ALL_EVENTS.map((ev) => (
                <div key={ev.id} className="flex items-center gap-2 rounded-lg border p-2 hover:bg-muted/40" data-testid={`new-webhook-event-${ev.id}`}>
                  <Checkbox id={`webhook-event-${ev.id}`} checked={events.includes(ev.id)} onCheckedChange={() => toggle(ev.id)} />
                  <Label htmlFor={`webhook-event-${ev.id}`} className="flex-1 cursor-pointer">
                    <div className="text-sm font-medium">{ev.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{ev.id}</div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !url || events.length === 0} data-testid="new-webhook-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealSecretDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [data]);
  if (!data) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.secret); setCopied(true); toast.success("Copied"); }
    catch { toast.error("Copy failed — write it down"); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reveal-webhook-secret-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-secondary" />
            {data.isRotation ? "Signing secret rotated" : "Save this signing secret"}
          </DialogTitle>
          <DialogDescription>
            Verify incoming requests by HMAC-SHA256 signing <code>&lt;timestamp&gt;.&lt;raw_body&gt;</code> with this secret.
            This is the only time you'll see it.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border bg-muted/30 p-6">
          <div className="break-all font-mono text-sm text-primary" data-testid="revealed-webhook-secret">{data.secret}</div>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            See <a href="/docs/webhooks" target="_blank" rel="noreferrer" className="underline">/docs/webhooks</a> for Python and Node verifier snippets.
          </AlertDescription>
        </Alert>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="copy-webhook-secret-btn">
            <Copy className="mr-1 h-4 w-4" /> {copied ? "Copied" : "Copy secret"}
          </Button>
          <Button onClick={onClose} data-testid="reveal-webhook-secret-done">I've saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditWebhook({ sub, onClose, onSaved }) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (sub) { setUrl(sub.url); setDescription(sub.description || ""); setEvents(sub.events || []); setErr(""); }
  }, [sub]);

  if (!sub) return null;

  const toggle = (id) => setEvents((es) => (es.includes(id) ? es.filter((x) => x !== id) : [...es, id]));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/tenant/webhooks/${sub.id}`, { url, description, events });
      toast.success("Updated");
      onSaved(); onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!sub} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="edit-webhook-dialog">
        <DialogHeader>
          <DialogTitle>Edit subscription</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="edit-webhook-url" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Events</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ALL_EVENTS.map((ev) => (
                <label key={ev.id} className="flex cursor-pointer items-center gap-2 rounded-lg border p-2 hover:bg-muted/40">
                  <Checkbox checked={events.includes(ev.id)} onCheckedChange={() => toggle(ev.id)} />
                  <div className="text-sm">
                    <div className="font-medium">{ev.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">{ev.id}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !url || events.length === 0} data-testid="edit-webhook-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveriesDrawer({ sub, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const load = async () => {
    if (!sub) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tenant/webhooks/${sub.id}/deliveries`, { params: { page_size: 50 } });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sub?.id]);

  const replay = async (d) => {
    try {
      await api.post(`/tenant/webhooks/${sub.id}/deliveries/${d.id}/replay`);
      toast.success("Queued replay");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Sheet open={!!sub} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto sm:max-w-2xl" data-testid="deliveries-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" /> Recent deliveries
          </SheetTitle>
          <SheetDescription className="break-all">{sub?.url}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Last {items.length} attempts</div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="deliveries-refresh">
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <div className="mt-3 space-y-3">
          {loading && <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
          {!loading && items.length === 0 && (
            <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No deliveries yet. Click "Test" from the row to fire a hello.world event.
            </div>
          )}
          {items.map((d) => {
            const Icon = d.status === "succeeded" ? CheckCircle2 : d.status === "failed" ? XCircle : Clock;
            const tone = d.status === "succeeded" ? "text-success" : d.status === "failed" ? "text-red-600" : "text-amber-600";
            return (
              <div key={d.id} className="rounded-xl border p-4" data-testid={`delivery-row-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`rounded-full capitalize ${DELIVERY_STATUS_TONE[d.status]}`}>{d.status}</Badge>
                      <Badge variant="outline" className="rounded-full text-[10px]">{d.event_type}</Badge>
                      <span className="text-xs text-muted-foreground tabular-nums">attempts: {d.attempts}</span>
                      {d.last_response_status && <span className={`text-xs tabular-nums ${tone}`}>HTTP {d.last_response_status}</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon className={`h-3.5 w-3.5 ${tone}`} />
                      <span className="font-mono">{d.id}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                      Created {new Date(d.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {d.next_attempt_at && d.status === "pending" && (
                        <> · next try {new Date(d.next_attempt_at).toLocaleString([], { hour: "2-digit", minute: "2-digit" })}</>
                      )}
                    </div>
                    {d.last_response_body_truncated && (
                      <pre className="mt-2 max-h-24 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-2 font-mono text-[11px] text-muted-foreground">
                        {d.last_response_body_truncated}
                      </pre>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setPayload(d)} data-testid={`delivery-view-${d.id}`}>
                      <FileJson2 className="mr-1 h-3.5 w-3.5" /> Payload
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => replay(d)} data-testid={`delivery-replay-${d.id}`}>
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> Replay
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <PayloadDialog delivery={payload} onClose={() => setPayload(null)} />
      </SheetContent>
    </Sheet>
  );
}

function PayloadDialog({ delivery, onClose }) {
  const pretty = useMemo(
    () => (delivery ? JSON.stringify(delivery.payload, null, 2) : ""),
    [delivery],
  );
  if (!delivery) return null;
  return (
    <Dialog open={!!delivery} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-hidden" data-testid="payload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileJson2 className="h-5 w-5" /> Payload — {delivery.event_type}</DialogTitle>
          <DialogDescription>Delivery <span className="font-mono">{delivery.id}</span></DialogDescription>
        </DialogHeader>
        <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded-lg border bg-muted/40 p-4 font-mono text-xs">{pretty}</pre>
        <DialogFooter>
          <Button variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(pretty); toast.success("Copied"); } catch { toast.error("Copy failed"); } }}>
            <Copy className="mr-1 h-4 w-4" /> Copy JSON
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ sub, onClose, onConfirm }) {
  if (!sub) return null;
  return (
    <Dialog open={!!sub} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="webhook-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete subscription?</DialogTitle>
          <DialogDescription>
            <span className="break-all font-mono">{sub.url}</span> will stop receiving events immediately.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => onConfirm(sub)} data-testid="webhook-delete-confirm">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
